import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  FormHelperText
} from '@mui/material';
import i18next from '../../lang';
import axios from 'axios';
import { isPasswordValid as checkPasswordFormat, PASSWORD_RULE_MESSAGE } from '../../utils/passwordValidator';

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);
  const [isKorean, setIsKorean] = useState(false);
  const [isJapanese, setIsJapanese] = useState(false);
  const [token, setToken] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const idParam = params.get('id');

    if (!tokenParam || !idParam) {
      setError(i18next.t('auth.invalid_reset_link'));
      return;
    }

    setToken(tokenParam);
    setUserId(idParam);
  }, [location]);

  const handleCapsLock = (e: KeyboardEvent) => {
    try {
      setIsCapsLockOn(e.getModifierState('CapsLock'));
    } catch (error) {
      console.log('Error checking CapsLock state:', error);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleCapsLock);
    document.addEventListener('keyup', handleCapsLock);

    return () => {
      document.removeEventListener('keydown', handleCapsLock);
      document.removeEventListener('keyup', handleCapsLock);
    };
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'newPassword') {
      setNewPassword(value);
    } else {
      setConfirmPassword(value);
    }

    const isKoreanKeyboard = window.navigator.language === 'ko-KR';
    const isJapaneseKeyboard = window.navigator.language === 'ja-JP';

    if (e.nativeEvent instanceof CompositionEvent) {
      setIsKorean(isKoreanKeyboard);
      setIsJapanese(isJapaneseKeyboard);
    }
  };

  const getInputWarning = () => {
    const warnings = [];
    if (isCapsLockOn) warnings.push(i18next.t('auth.capslock_on'));
    if (isKorean) warnings.push(i18next.t('auth.korean_input'));
    if (isJapanese) warnings.push(i18next.t('auth.japanese_input'));
    return warnings.join('、');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError(i18next.t('error.enter_password'));
      return;
    }

    if (!checkPasswordFormat(newPassword)) {
      setError(PASSWORD_RULE_MESSAGE);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(i18next.t('error.password_mismatch'));
      return;
    }

    try {
      await axios.post('/api/v1/auth/reset-password', {
        token,
        userId,
        newPassword
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || i18next.t('auth.password_reset_failed'));
    }
  };

  const isPasswordFormValid = () => {
    return newPassword !== '' && checkPasswordFormat(newPassword) && newPassword === confirmPassword;
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {i18next.t('auth.password_change_success')}
          </Alert>
          <Typography>
            {i18next.t('auth.redirecting_to_login')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
          {i18next.t('auth.password_reset')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label={i18next.t('user.new_password')}
            type="password"
            placeholder="半角英数字8〜20文字"
            value={newPassword}
            onChange={handlePasswordChange}
            onCompositionStart={(e) => {
              const lang = e.currentTarget.lang;
              setIsKorean(lang === 'ko');
              setIsJapanese(lang === 'ja');
            }}
            onCompositionEnd={() => {
              setIsKorean(false);
              setIsJapanese(false);
            }}
            error={newPassword !== '' && !checkPasswordFormat(newPassword)}
            helperText={newPassword !== '' && !checkPasswordFormat(newPassword) ? PASSWORD_RULE_MESSAGE : '半角英数字8〜20文字'}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label={i18next.t('auth.password_confirm_label')}
            type="password"
            value={confirmPassword}
            onChange={handlePasswordChange}
            onCompositionStart={(e) => {
              const lang = e.currentTarget.lang;
              setIsKorean(lang === 'ko');
              setIsJapanese(lang === 'ja');
            }}
            onCompositionEnd={() => {
              setIsKorean(false);
              setIsJapanese(false);
            }}
            error={confirmPassword !== '' && newPassword !== confirmPassword}
            helperText={confirmPassword !== '' && newPassword !== confirmPassword ? i18next.t('error.password_mismatch') : ''}
          />

          {(isCapsLockOn || isKorean || isJapanese) && (
            <FormHelperText error sx={{ ml: 1 }}>
              {getInputWarning()}
            </FormHelperText>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={!isPasswordFormValid()}
          >
            {i18next.t('user.change_password')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default PasswordReset; 